import { TextLinkWidget } from '@tableau/widgets';
import { useFormikContext } from 'formik';
import React from 'react';

import { FormikCheckbox } from '~commonInfra/components/FormikCheckbox';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { HelpService } from '~commonInfra/services/HelpService';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { TABLEAU_GAI_FIELD } from '~src/settings/general-settings/components/sections/pulse/TableauGAISection';
import {
  useGenerativeAIRegistrationHook
} from '~src/settings/general-settings/components/sections/tableau-gai/GenerativeAIRegistrationApi';

import { SessionServerSelectors } from '../../../../../bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '../../../../../codegen/Localize';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { learnMoreStyle } from '../../../../styles/CommonStyles';
import { useSelector } from '../../../utils';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '../../site-settings/ReactSiteSettingsForSiteAdmin';
import { GenerativeAIRegistration } from './GenerativeAIRegistration';

export const TABLEAU_GAI_PULSE_FIELD = 'settings.generativeAiPulse';
export const TABLEAU_GAI_PULSE_DISCOVER_FIELD = 'settings.generativeAiPulseDiscoverEnabled';
export const TABLEAU_GAI_PULSE_MULTILINGUAL_ASK_FIELD = 'settings.generativeAiPulseExternalEmbeddingsEnabled';
export const TABLEAU_GAI_PREP_FIELD = 'settings.generativeAiPrep';
export const TABLEAU_GAI_CATALOG_FIELD = 'settings.generativeAiDataCatalog';
export const TABLEAU_GAI_AUTHORING_FIELD = 'settings.generativeAiWebAuthoring';

type InputAttrs = React.InputHTMLAttributes<HTMLInputElement>;

export type GenerativeAISectionProps = SiteSettingsForSiteAdminProps<'generativeAiEnabled'
  | 'generativeAiPulseExternalEmbeddingsEnabled' | 'generativeAiPulse' | 'generativeAiPulseDiscoverEnabled'
  | 'generativeAiPrep' | 'generativeAiDataCatalog' | 'generativeAiWebAuthoring' | 'siteSettingsV2'>;

export function GenerativeAISection(props: GenerativeAISectionProps) {

  const formik = useFormikContext<GenerativeAISectionProps>();

  function getDescription(tableauGAIHelpUrl: string) {
    return <>
      <p id='tableau-gai-intruduction-id'>
        {Messages.tableauGAISiteSettingIntroduction()}
      </p>
      <div id='tableau-gai-warning-id'>
        <p style={{ display: 'inline' }}>{Messages.tableauGAISiteSettingWarning()}</p>
        <TextLinkWidget
          className={learnMoreStyle}
          href={tableauGAIHelpUrl}
          target='_blank'
          testId={'tableau-gai-learn-more'}
        >
          {Messages.learnMore()}
        </TextLinkWidget>
      </div>
    </>;
  }

  /**
   * Forces checkboxes to be unselected when site is not registered
   * Passing the value in to prevent formik rules from being broken.
   * Intentional design by the SLAP team.
   */
  function forceUncheckedIf(unchecked: boolean): Partial<InputAttrs> {
    return {
      checked: unchecked ? false : undefined
    };
  }

  function PulseStandard() {
    return FeatureFlags.generativeAiSiteControl ?
      <FormikCheckbox
        name={TABLEAU_GAI_FIELD}
        label={Messages.tableauGAISiteSettingsFeaturePulseStandard()}
        testId={'tableau-gai-checkbox'} />
      :
      null;
  }

  function PulseMultilingualAsk() {
    return FeatureFlags.ntbueMultilingualAdHocQa ?
      <FormikCheckbox
        name={TABLEAU_GAI_PULSE_MULTILINGUAL_ASK_FIELD}
        label={Messages.tableauGAISiteSettingsFeaturePulseMultilingualAsk()}
        testId={'tableau-gai-pulse-multilingual-ask-checkbox'} />
      :
      null;
  }

  function PulseEnhanced(enabled: boolean, isTrial: boolean) {
    const label = isTrial ? '*' + Messages.tableauGAISiteSettingsFeaturePulse() : Messages.tableauGAISiteSettingsFeaturePulse();

    return FeatureFlags.ntbueAIEnhancedLanguage ?
      <FormikCheckbox name={TABLEAU_GAI_PULSE_FIELD}
        label={label}
        testId={'tableau-gai-pulse-checkbox'}
        disabled={!enabled}
        {...forceUncheckedIf(!enabled)}
      />
      : null;
  }

  function PulseDiscover(enabled: boolean, isTrial: boolean) {
    const label = isTrial ? '*' + Messages.tableauGAISiteSettingsFeaturePulseDiscover() : Messages.tableauGAISiteSettingsFeaturePulseDiscover();

    return FeatureFlags.ntbueDiscoverFromHomepageGroup ?
      <FormikCheckbox name={TABLEAU_GAI_PULSE_DISCOVER_FIELD}
        label={label}
        testId={'tableau-gai-pulse-discover-checkbox'}
        disabled={!enabled}
        {...forceUncheckedIf(!enabled)}
      />
      : null;
  }

  function Authoring(enabled: boolean, isTrial: boolean) {
    const label = isTrial ? '*' + Messages.tableauGAISiteSettingsFeatureAuthoring() : Messages.tableauGAISiteSettingsFeatureAuthoring();

    return FeatureFlags.webAuthoringAISettingEnabled ?
      <FormikCheckbox name={TABLEAU_GAI_AUTHORING_FIELD}
        label={label}
        testId={'tableau-gai-authoring-checkbox'}
        disabled={!enabled}
        {...forceUncheckedIf(!enabled)}
      />
      : null;
  }

  function Prep(enabled: boolean, isTrial: boolean) {
    const tableauGAISiteSettingsString = FeatureFlags.einsteinCopilotForPrep ? Messages.tableauGAISiteSettingsFeaturePrepFlow() : Messages.tableauGAISiteSettingsFeaturePrep();
    const label = isTrial ? '*' + tableauGAISiteSettingsString : tableauGAISiteSettingsString;

    return <FormikCheckbox name={TABLEAU_GAI_PREP_FIELD}
      label={label}
      testId={'tableau-gai-prep-checkbox'}
      disabled={!enabled}
      {...forceUncheckedIf(!enabled)}
    />;
  }

  function DataCatalog(enabled: boolean, isTrial: boolean) {
    const label = Messages.tableauGAISiteSettingsFeatureCatalog();

    return <FormikCheckbox name={TABLEAU_GAI_CATALOG_FIELD}
      label={label}
      testId={'tableau-gai-catalog-checkbox'}
      disabled={!enabled || isTrial}
      {...forceUncheckedIf(!enabled || isTrial)}
    />;
  }

  function getTrialSection() {
    return <>
      <p id="generative-ai-setting-trial"><i>{Messages.tableauGAISiteSettingsTrial()}</i></p>
    </>;
  }

  function getLegalSection() {

    const sfLegalUrl = 'https://www.salesforce.com/company/legal/trust-and-compliance-documentation/';

    return <>
      <p id="generative-ai-setting-lg-terms-1">{Messages.tableauGAISiteSettingsLTerm1()}</p>
      <div id="generative-ai-setting-lg-terms-2">{Messages.tableauGAISiteSettingsLTerm2()}
        <ul>
          <li id="generative-ai-setting-lg-terms-3">{Messages.tableauGAISiteSettingsLTerm3()}</li>
          <li id="generative-ai-setting-lg-terms-4">{Messages.tableauGAISiteSettingsLTerm4()}</li>
        </ul>
      </div>
      <div id="generative-ai-setting-lg-terms-5">{Messages.tableauGAISiteSettingsLTerm5()}
        <TextLinkWidget
          className={learnMoreStyle}
          href={sfLegalUrl}
          target="_blank"
          testId={'tableau-gai-learn-more'}
        >
          {Messages.tableauGAISiteSettingsLTerm6()}
        </TextLinkWidget>
      </div>
    </>;
  }

  const { registrationState, registerEnterprise, unregisterEnterprise } = useGenerativeAIRegistrationHook(formik);

  const tableauGAIHelpUrl = HelpService.getHelpUrlByCategory('gpt-core');

  const isOnlineEnvironment = useSelector(SessionServerSelectors.isOnlineEnvironment);

  const showGAISection = FeatureFlags.tabGAITokenService && isOnlineEnvironment;

  const siteTypeName = getSessionManagementServiceInstance().siteTypeName();
  const isTrial = FeatureFlags.ect_UseSharedOrgForTrialEnabled && siteTypeName === 'Trial';
  const showGAIFeatures = !FeatureFlags.tabGAITokenServiceRegistration;
  const hasEntitlements = FeatureFlags.analyticsAssistantCheckEntitlements ? formik.values.settings.siteSettingsV2?.einsteinCopilotForTableauEnabled.toLowerCase() === 'true' : true;

  const generativeAiCheckboxesEnabled = (registrationState.registered && hasEntitlements) || showGAIFeatures || isTrial;

  return !showGAISection ? null : <SettingsSection title={Messages.tableauGAISiteSettingTitle()}
    testId={'generative-ai-setting-section'}
    description={getDescription(tableauGAIHelpUrl)}>

    {isTrial ? null :
      <GenerativeAIRegistration name={'generative-ai-setting-registration'}
        testId={'generative-ai-setting-registration'}
        gaiHook={{ registrationState, registerEnterprise, unregisterEnterprise }}
      />}

    <p>{Messages.tableauGAISiteSettingFeatureChoice()}</p>

    {PulseStandard()}
    {PulseMultilingualAsk()}
    {PulseEnhanced(generativeAiCheckboxesEnabled, isTrial)}
    {PulseDiscover(generativeAiCheckboxesEnabled, isTrial)}
    {Authoring(generativeAiCheckboxesEnabled, isTrial)}
    {Prep(generativeAiCheckboxesEnabled, isTrial)}
    {DataCatalog(generativeAiCheckboxesEnabled, isTrial)}

    {isTrial ? getTrialSection() : null}

    {getLegalSection()}

  </SettingsSection>;
}

export const GenerativeAISectionSpecs = componentRegistryEntry({
  name: 'GenerativeAISection',
  component: GenerativeAISection
});
